import { getApiUrl, getBackendUrl } from "@/utils/api";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, GripVertical, Pencil, Play } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";
import ImageEditor from "@/components/ImageEditor";

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = getApiUrl();

// Detect whether a URL points to a video by extension
const VIDEO_EXTS = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;
const isVideoUrl = (url) => typeof url === "string" && VIDEO_EXTS.test(url);

// Sortable Image Item Component
const SortableImageItem = ({ id, url, index, onRemove, onEdit }) => {
  const videoRef = useRef(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Ensure video loads when component mounts or URL changes
  useEffect(() => {
    if (isVideoUrl(url) && videoRef.current) {
      videoRef.current.load();
    }
  }, [url]);

  // Check if image is uploaded to platform (editable) or external link (not editable)
  const isUploadedImage = url && (
    url.startsWith('/api/uploads/') || 
    url.startsWith('/uploads/') ||
    url.includes(getBackendUrl())
  );
  
  // Only allow editing for uploaded images (not external links)
  const canEdit = onEdit && !isVideoUrl(url) && isUploadedImage;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 1,
    touchAction: 'none', // Prevent scroll during drag
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative group aspect-square bg-muted border-2 cursor-grab active:cursor-grabbing ${isDragging ? 'border-primary shadow-lg' : 'border-transparent hover:border-primary/50'}`}
      data-testid={`image-preview-${index}`}
    >
      {/* Visual drag indicator */}
      <div className="absolute top-1 left-1 p-1.5 bg-black/70 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 pointer-events-none z-20">
        <GripVertical size={14} />
        <span className="text-xs">Drag</span>
      </div>
      
      {/* Image Number Badge */}
      <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/70 text-white text-xs font-medium rounded z-20 pointer-events-none">
        {index + 1}
      </div>
      
      {/* First image indicator */}
      {index === 0 && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-white text-xs font-medium rounded z-20 pointer-events-none">
          Featured
        </div>
      )}
      
      <img
        src={resolveImageUrl(url)}
        alt={`Preview ${index + 1}`}
        className={`w-full h-full object-cover pointer-events-none ${isVideoUrl(url) ? "hidden" : ""}`}
        draggable={false}
        onError={(e) => {
          e.target.src = "https://via.placeholder.com/150?text=Error";
        }}
      />
      {isVideoUrl(url) && (
        <>
          <video
            ref={videoRef}
            src={resolveImageUrl(url)}
            className="w-full h-full object-cover pointer-events-none bg-black"
            muted
            playsInline
            preload="metadata"
            onError={(e) => {
              console.error(`Video load error for ${url}:`, e);
            }}
            onLoadedMetadata={(e) => {
              // Seek to 1 second to get a better thumbnail frame
              if (e.target.duration > 1) {
                e.target.currentTime = 1;
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/60 rounded-full p-2">
              <Play className="h-5 w-5 text-white" fill="white" />
            </div>
          </div>
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded z-20 pointer-events-none">
            VIDEO
          </div>
        </>
      )}
      
      {canEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onEdit(index);
          }}
          className="absolute bottom-1 right-1 p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-30"
          data-testid={`edit-image-${index}`}
          title="Edit image"
        >
          <Pencil size={14} />
        </button>
      )}

      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove(index);
          }}
          className="absolute top-1 right-1 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-30"
          data-testid={`remove-image-${index}`}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

// Drag Overlay Image
const DragOverlayImage = ({ url }) => {
  return (
    <div className="aspect-square bg-muted ring-2 ring-primary shadow-2xl w-32 h-32 relative">
      {isVideoUrl(url) ? (
        <>
          <video 
            src={resolveImageUrl(url)} 
            className="w-full h-full object-cover bg-black" 
            muted 
            playsInline 
            preload="metadata"
            onLoadedMetadata={(e) => {
              if (e.target.duration > 1) {
                e.target.currentTime = 1;
              }
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Play className="h-6 w-6 text-white" fill="white" />
          </div>
        </>
      ) : (
        <img
          src={resolveImageUrl(url)}
          alt="Dragging"
          className="w-full h-full object-cover"
          draggable={false}
        />
      )}
    </div>
  );
};

export const ImageUpload = ({ 
  onUpload, 
  multiple = false, 
  currentImages = [],
  onRemove,
  onReorder,
  onReplace,
  label = "Upload Images",
  // Opt-IN: only the Edit Property gallery uploader passes
  // skipWatermark={false} — every other use of this shared component
  // (CMS images, logos, icons, floorplans, categories, etc.) must stay
  // unwatermarked.
  skipWatermark = true,
  testId = "image-upload"
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const fileInputRef = useRef(null);

  // Generate unique IDs for each image (using index as suffix to handle duplicates)
  const imageIds = currentImages.map((url, index) => `${index}-${url}`);

  // DnD Kit sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    const onUploadProgress = (e) => {
      if (e.total) {
        setUploadProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };
    try {
      const formData = new FormData();
      
      if (multiple) {
        Array.from(files).forEach(file => {
          formData.append("files", file);
        });
        
        const response = await axios.post(
          `${API}/admin/upload-multiple?skip_watermark=${skipWatermark}`,
          formData,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
          }
        );

        if (response.data.uploaded && response.data.uploaded.length > 0) {
          const urls = response.data.uploaded.map(f => f.url);
          onUpload(urls);
          const vidCount = response.data.uploaded.filter(f => f.is_video).length;
          const imgCount = response.data.uploaded.length - vidCount;
          const parts = [];
          if (imgCount) parts.push(`${imgCount} image${imgCount > 1 ? "s" : ""}`);
          if (vidCount) parts.push(`${vidCount} video${vidCount > 1 ? "s" : ""}`);
          toast.success(`${parts.join(" + ") || "File(s)"} uploaded`);
        }
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach((er) => {
            toast.error(`${er.file}: ${er.reason}`, { duration: 7000 });
          });
        } else if (!response.data.uploaded || response.data.uploaded.length === 0) {
          toast.error("No files were uploaded");
        }
      } else {
        formData.append("file", files[0]);
        
        const response = await axios.post(
          `${API}/admin/upload?skip_watermark=${skipWatermark}`,
          formData,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
          }
        );

        if (response.data.url) {
          onUpload(response.data.url);
          toast.success(response.data.is_video ? "Video uploaded" : "Image uploaded");
        } else {
          toast.error("Upload failed - no URL returned");
        }
      }
      setUploadProgress(100);
    } catch (error) {
      console.error("Upload error:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to upload file(s)");
    } finally {
      setUploading(false);
      // Briefly show 100% before hiding so the user sees completion
      setTimeout(() => setUploadProgress(0), 600);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // DnD Kit handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id && onReorder) {
      const oldIndex = imageIds.indexOf(active.id);
      const newIndex = imageIds.indexOf(over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(currentImages, oldIndex, newIndex);
        onReorder(newOrder);
        toast.success("Image order updated");
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get the actual URL from the active ID
  const getUrlFromId = (id) => {
    if (!id) return null;
    const index = imageIds.indexOf(id);
    return index !== -1 ? currentImages[index] : null;
  };

  const activeUrl = getUrlFromId(activeId);

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Upload Area */}
      <div
        className={`border border-dashed rounded-none px-4 py-3 text-center transition-colors cursor-pointer ${
          dragOver 
            ? "border-primary bg-primary/5" 
            : "border-border hover:border-primary/50"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          multiple={multiple}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          data-testid="file-input"
        />
        
        {uploading ? (
          <div className="flex items-center gap-3 w-full max-w-md mx-auto">
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
            <span className="text-xs text-muted-foreground shrink-0">Uploading {uploadProgress}%</span>
            <div className="flex-1 h-1.5 bg-muted/40 overflow-hidden rounded-sm" data-testid="upload-progress">
              <div
                className="h-full bg-primary transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
                data-testid="upload-progress-bar"
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium">{label}</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">— drag &amp; drop or click</span>
          </div>
        )}
      </div>

      {/* Sortable Preview Grid */}
      {currentImages && currentImages.length > 0 && (
        <div className="space-y-2">
          {onReorder && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 bg-blue-50 text-blue-700 p-2 rounded">
              <GripVertical size={14} /> 
              <strong>Tip:</strong> Drag and drop images to reorder. The first image will be the featured image.
            </p>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={imageIds} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-4 gap-3">
                {currentImages.map((url, index) => (
                  <SortableImageItem
                    key={imageIds[index]}
                    id={imageIds[index]}
                    url={url}
                    index={index}
                    onRemove={onRemove}
                    onEdit={onReplace ? setEditingIndex : null}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeUrl ? <DragOverlayImage url={activeUrl} /> : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      <ImageEditor
        open={editingIndex !== null}
        onClose={() => setEditingIndex(null)}
        sourceUrl={editingIndex !== null ? currentImages[editingIndex] : null}
        skipWatermark={skipWatermark}
        onSaved={(newUrl) => {
          if (onReplace && editingIndex !== null) onReplace(editingIndex, newUrl);
        }}
      />
    </div>
  );
};

export default ImageUpload;
